import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Show } from '../../components/show';

import Button from '../../components/button';
import AssignmentTimeWarning from '../../components/assignment-time-warning';
import FluidContainer from '../../components/layouts/components/fluid-container';
import Spacer from '../../components/spacer';
import { Body, Heading, Interactive, Helper } from '../../components/typography';

import { RESUME_HOMEWORK_MODAL } from '../modal/modal-types';

import { getStatusArrayByAssignmentId, startHomework } from '../../actions/status';
import { showModal } from '../../actions/modal';

import { HOMEWORK_EXECUTION } from '../../constants/routes';
import { SOURCES } from '../../constants/sources';

import { getSourceLabel, printTime, textAssignmentEndDate } from '../../lib/utils';
import Stack from '../../components/stack';
import { GotoSourceButton } from '../../components/buttons-bar';
import { useOnlineStatus } from '../../lib/hooks';
import { getCurrentSection } from '../../lib/status';

const textPoints = (points) => {
    if (points === 1 || points === 1.0) {
        return '1 punto';
    }
    return `${points} punti`;
};

const textActionMessage = (resumed, source) => {
    if (resumed) {
        return 'Forse hai chiuso la finestra del browser: ti abbiamo perso di vista e il tempo a disposizione ha continuato a scorrere. Vuoi riprendere?';
    }

    return `Puoi continuare a svolgerla adesso o tornare su  ${getSourceLabel(source)} e continuare in un altro momento.`;
};

export const textRepeats = (maxAttemptsNumber, attemptsNumber) => {
    if (maxAttemptsNumber <= 0) {
        return 'puoi svolgere la prova tutte le volte che vuoi.';
    }

    const remainingAttemptsNumber = maxAttemptsNumber - attemptsNumber + 1;

    if (remainingAttemptsNumber === 1) {
        return `hai a disposizione 1 tentativo: puoi usarlo anche in un altro momento.`;
    }

    return `hai a disposizione ${remainingAttemptsNumber} tentativi: puoi usarli anche in un altro momento.`;
};

export const PausePage = ({
    assignmentEndDate,
    assignmentId,
    attemptsNumber,
    bookId,
    getStatusArrayByAssignmentId,
    googleCourseWorkId,
    homeworkTime,
    initialTime,
    isApplicationEmbedded,
    isOpenAssignment,
    leftTime,
    maxAttemptsNumber,
    saveStatus,
    showModal,
    source,
    startHomework,
    status = {},
    homeworkId,
}) => {
    useEffect(() => {
        if (assignmentId && maxAttemptsNumber > 0 && source !== SOURCES.LAZE) {
            getStatusArrayByAssignmentId(assignmentId);
        }
    }, [assignmentId, maxAttemptsNumber, getStatusArrayByAssignmentId, source]);

    const onlineStatus = useOnlineStatus();

    const history = useNavigate();

    const formattedLeftTime = printTime(leftTime);
    const formattedInitialTime = printTime(initialTime);

    const backSource = !!googleCourseWorkId ? SOURCES.GOOGLE_CLASSROOM : source;

    const resumeOnClick = () => {
        if (status.resumed || !!status.expiringAssignment) {
            startHomework();
            setTimeout(history, 0, HOMEWORK_EXECUTION, { replace: true });
        } else {
            showModal(RESUME_HOMEWORK_MODAL);
        }
    };

    return (
        <FluidContainer flexDirection="column" maxWidth={['100%', '50%']} paddingTop={[232, 208, 168]}>
            <Body lv={4} weight="semi-bold">
                {`PROVA IN ${status.resumed ? 'CORSO' : 'PAUSA'}`}
            </Body>

            <Spacer size="small" />
            <Show when={assignmentEndDate}>
                <Body lv={3} weight="semi-bold">
                    {textAssignmentEndDate(assignmentEndDate)}
                </Body>
            </Show>
            <Spacer size="medium" />

            <Heading lv={2} weight="light">
                {'Vuoi riprendere la prova?'}
            </Heading>
            <Spacer size="small" />
            <Show when={saveStatus && (status.resumed || !isApplicationEmbedded)}>
                <Body lv={3}>{textActionMessage(status.resumed, backSource)}</Body>
            </Show>
            <Show when={status.expiringAssignment}>
                <AssignmentTimeWarning time={homeworkTime} remainingTime={status.remainingTime} />
            </Show>

            <Spacer size="medium" />
            <Show when={initialTime > 0 && !status.expiringAssignment}>
                <Body lv={3} weight="semi-bold">{`Tempo rimasto: ${formattedLeftTime}`}</Body>
                <Helper>{`Tempo iniziale: ${formattedInitialTime}`}</Helper>
                <Spacer size="medium" />
            </Show>

            <Body lv={3}>
                {'Ricorda:'}
                <ul>
                    <li>{'una risposta saltata viene esclusa dal voto;'}</li>
                    <li>{'una risposta incompleta conta come un errore;'}</li>
                    {source === SOURCES.ZTE_UNITUTOR && status.scoreModifier && (
                        <li>{`un esercizio giusto vale +${textPoints(status.scoreModifier.correct)}, uno sbagliato vale ${textPoints(
                            status.scoreModifier.wrong,
                        )};`}</li>
                    )}
                    <li>{textRepeats(maxAttemptsNumber, attemptsNumber)}</li>
                </ul>
            </Body>

            <Show when={source !== SOURCES.TT && source !== SOURCES.ZTE_UNITUTOR}>
                <Body weight="semi-bold">{'Non ricordi come si calcola il punteggio?'}</Body>
                <Interactive as="a" href="https://assistenza.zanichelli.it/support/solutions/folders/4000035503" target="_blank">
                    {'Leggi le domande frequenti.'}
                </Interactive>
            </Show>

            <Spacer size="xl" />
            <Stack gap="small" direction="horizontal">
                <Button onClick={resumeOnClick} size="small" disabled={!onlineStatus}>
                    {'Riprendi'}
                </Button>
                <Show when={!isApplicationEmbedded}>
                    <GotoSourceButton
                        bookId={bookId}
                        source={backSource}
                        homeworkId={!isOpenAssignment ? homeworkId : undefined}
                        type="tertiary"
                    />
                </Show>
            </Stack>
        </FluidContainer>
    );
};

PausePage.propTypes = {
    assignmentEndDate: PropTypes.string,
    assignmentId: PropTypes.string,
    attemptsNumber: PropTypes.number,
    bookId: PropTypes.number,
    getStatusArrayByAssignmentId: PropTypes.func,
    googleCourseWorkId: PropTypes.string,
    homeworkTime: PropTypes.number,
    initialTime: PropTypes.number,
    isApplicationEmbedded: PropTypes.bool,
    isOpenAssignment: PropTypes.bool,
    leftTime: PropTypes.number,
    maxAttemptsNumber: PropTypes.number,
    saveStatus: PropTypes.bool,
    showModal: PropTypes.func,
    source: PropTypes.string,
    startHomework: PropTypes.func,
    status: PropTypes.object,
    homeworkId: PropTypes.string,
};

const mapStateToProps = (state) => {
    return {
        assignmentEndDate: state.assignment.endDate,
        assignmentId: state.application.assignmentId,
        attemptsNumber: state.status.attemptsNumber,
        bookId: state.application.bookId,
        googleCourseWorkId: state.application.googleCourseWorkId,
        homeworkTime: state.application.time,
        initialTime: !!state.status.sections ? getCurrentSection(state.status.sections).time : state.application.time,
        isApplicationEmbedded: state.application.isEmbedded,
        isOpenAssignment: state.application.isOpenAssignment,
        leftTime: state.status.remainingTime,
        maxAttemptsNumber: state.application.repeatTest,
        saveStatus: state.application.saveStatus,
        source: state.application.source,
        homeworkId: state.homework.id,
        status: state.status,
    };
};

const actionCreators = {
    showModal,
    startHomework,
    getStatusArrayByAssignmentId,
};

export default connect(mapStateToProps, actionCreators)(PausePage);
